import { useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  IconButton,
  Menu,
  Portal,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { Tooltip } from '@/components/ui/tooltip';
import { FiCopy, FiTrash2, FiRefreshCw, FiSlash, FiMoreVertical } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useLobbyDayAttendees, useRemoveAttendee, useRotateAttendeeToken, useRevokeAttendeeToken, useSetDeliveryMode } from '../../../hooks/useLobbyDay'
import { extractApiMessage } from '../../../lib/apiError'
import { publicOrigin } from '../../../lib/native/instance'
import { ResponsiveModal, ResponsiveTable, type ResponsiveColumn } from '../../shared'
import { AttendeeTagChips } from '../AttendeeTagChips'
import { ConductTagQuickAdd } from '../ConductTagQuickAdd'
import { ContactDetailModal } from '../../contactDetail/ContactDetailModal'
import type { AddedAttendeeToken, AttendeeDeliveryMode, LobbyDayAttendeeDto, LobbyDayStatus } from '../../../types/lobbyDay'

interface RosterViewProps {
  eventId: string
  eventStatus: LobbyDayStatus
  // Show-once raw tokens minted by an add-attendees call that just completed (LW-24) -- lets a
  // freshly-added attendee's row offer Copy link immediately instead of Rotate link. Optional:
  // a plain reload (or arriving at Roster without having just added anyone) has none, and every
  // row simply starts in the Rotate link state.
  freshTokens?: AddedAttendeeToken[]
}

export function RosterView({ eventId, eventStatus, freshTokens }: RosterViewProps) {
  const { data: attendees, isLoading, error } = useLobbyDayAttendees(eventId)
  const removeMutation = useRemoveAttendee(eventId)
  const rotateMutation = useRotateAttendeeToken(eventId)
  const revokeMutation = useRevokeAttendeeToken(eventId)
  const setModeMutation = useSetDeliveryMode(eventId)
  const navigate = useNavigate()
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [rotatingId, setRotatingId] = useState<string | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<LobbyDayAttendeeDto | null>(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [revokeError, setRevokeError] = useState<string | null>(null)
  const [settingModeId, setSettingModeId] = useState<string | null>(null)
  const [openContactId, setOpenContactId] = useState<string | null>(null)
  // Show-once raw tokens, keyed by attendeeId, held only in memory for this session. The backend
  // never re-exposes a raw token once minted (LW-24 grant-only convergence) -- a row with an
  // entry here was just added or just rotated and can still be copied; any other row has no
  // re-copyable link and must be rotated to mint a fresh one.
  const [tokenByAttendee, setTokenByAttendee] = useState<Record<string, string>>({})

  // Seed the map from a just-completed add so freshly-added rows land in the Copy link state.
  useEffect(() => {
    if (!freshTokens || freshTokens.length === 0) return
    setTokenByAttendee((prev) => {
      const next = { ...prev }
      for (const t of freshTokens) next[t.attendeeId] = t.rawToken
      return next
    })
  }, [freshTokens])

  // [Native B1] The instance's origin. Null on native with no instance: no correct link exists, so
  // copy AND rotate are disabled (a rotate would kill the old link with no way to share the new).
  const origin = publicOrigin()

  const copyLink = (token: string) => {
    if (!origin) return
    navigator.clipboard.writeText(`${origin}/lobby/${token}`)
    toaster.create({ type: 'success', title: 'Attendee link copied' })
  }

  const rotateLink = async (attendeeId: string) => {
    if (!origin) return
    setRotatingId(attendeeId)
    try {
      const result = await rotateMutation.mutateAsync(attendeeId)
      setTokenByAttendee((prev) => ({ ...prev, [attendeeId]: result.accessToken }))
      navigator.clipboard.writeText(`${origin}/lobby/${result.accessToken}`)
      toaster.create({ type: 'success', title: 'New link generated and copied — the old link no longer works' })
    } finally {
      setRotatingId(null)
    }
  }

  const openRevoke = (a: LobbyDayAttendeeDto) => {
    setRevokeTarget(a)
    setRevokeReason('')
    setRevokeError(null)
  }
  const closeRevoke = () => {
    setRevokeTarget(null)
    setRevokeReason('')
    setRevokeError(null)
  }
  const confirmRevoke = async () => {
    if (!revokeTarget) return
    setRevokeError(null)
    try {
      await revokeMutation.mutateAsync({
        attendeeId: revokeTarget.attendeeId,
        body: { reasonCode: 'OrganizerRevoked', reasonNote: revokeReason.trim() || undefined },
      })
      toaster.create({ type: 'success', title: 'Link revoked' })
      closeRevoke()
    } catch (err) {
      setRevokeError(extractApiMessage(err, 'Failed to revoke this link.'))
    }
  }

  const removeAttendee = async (a: LobbyDayAttendeeDto) => {
    setRemovingId(a.attendeeId)
    try {
      await removeMutation.mutateAsync(a.attendeeId)
    } finally {
      setRemovingId(null)
    }
  }

  const setDeliveryMode = async (a: LobbyDayAttendeeDto, mode: AttendeeDeliveryMode) => {
    if (a.deliveryMode === mode) return
    setSettingModeId(a.attendeeId)
    try {
      await setModeMutation.mutateAsync({ attendeeId: a.attendeeId, mode })
      toaster.create({ type: 'success', title: `Delivery set to ${mode}` })
    } catch {
      toaster.create({ type: 'error', title: 'Could not update delivery mode' })
    } finally {
      setSettingModeId(null)
    }
  }

  const columns: ResponsiveColumn<LobbyDayAttendeeDto>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (a) => (
        <Text
          as="span"
          fontWeight="medium"
          cursor="pointer"
          onClick={(e) => { e.stopPropagation(); setOpenContactId(a.contactId) }}
        >
          {a.fullName}
        </Text>
      ),
    },
    {
      header: 'Districts',
      mobileRole: 'badge',
      cell: (a) =>
        a.hasVoterRecord ? (
          <HStack gap={1}>
            {a.stateHouseDistrict && <Badge>HD-{a.stateHouseDistrict}</Badge>}
            {a.stateSenateDistrict && <Badge>SD-{a.stateSenateDistrict}</Badge>}
            {a.congressionalDistrict && <Badge>CD-{a.congressionalDistrict}</Badge>}
            {!a.stateHouseDistrict && !a.stateSenateDistrict && !a.congressionalDistrict && (
              <Badge colorPalette="yellow">no district data</Badge>
            )}
          </HStack>
        ) : (
          <Tooltip content="No linked voter record — constituency shows as Unverified. Open the contact to match them.">
            <Badge colorPalette="yellow">district unknown</Badge>
          </Tooltip>
        ),
    },
    {
      header: 'Meetings',
      mobileRole: 'badge',
      isNumeric: true,
      cell: (a) => (
        <Badge colorPalette={a.meetingCount >= 3 && a.meetingCount <= 6 ? 'green' : a.meetingCount > 6 ? 'red' : 'yellow'}>
          {a.meetingCount}
        </Badge>
      ),
    },
    {
      header: 'Tags',
      mobileRole: 'meta',
      cell: (a) => (
        <HStack gap={1} align="center">
          <AttendeeTagChips tags={a.tags} contactId={a.contactId} eventId={eventId} />
          <ConductTagQuickAdd contactId={a.contactId} eventId={eventId} />
        </HStack>
      ),
    },
    {
      header: 'Delivery',
      mobileRole: 'meta',
      cell: (a) => (
        <HStack gap={1}>
          {(['App', 'Paper', 'Both'] as AttendeeDeliveryMode[]).map((mode) => (
            <Button
              key={mode}
              size="xs"
              minH="44px"
              variant={a.deliveryMode === mode ? 'solid' : 'outline'}
              colorPalette={a.deliveryMode === mode ? (mode === 'Paper' ? 'orange' : 'blue') : 'gray'}
              disabled={settingModeId === a.attendeeId}
              onClick={(e) => { e.stopPropagation(); setDeliveryMode(a, mode) }}
            >
              {mode}
            </Button>
          ))}
        </HStack>
      ),
    },
    {
      header: 'Schedule link',
      mobileRole: 'meta',
      cell: (a) => {
        const token = tokenByAttendee[a.attendeeId]
        return (
          <HStack gap={1}>
            {token ? (
              <Button size="xs" variant="outline" minH="44px" disabled={!origin} onClick={(e) => { e.stopPropagation(); copyLink(token) }}>
                <FiCopy />Copy link
              </Button>
            ) : (
              <Button
                size="xs"
                variant="outline"
                minH="44px"
                disabled={!origin}
                loading={rotatingId === a.attendeeId}
                onClick={(e) => { e.stopPropagation(); rotateLink(a.attendeeId) }}
              >
                <FiRefreshCw />Rotate link
              </Button>
            )}
            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton
                  aria-label="Link actions"
                  size="xs"
                  variant="ghost"
                  minW="44px"
                  h="44px"
                  loading={rotatingId === a.attendeeId}
                  onClick={(e) => e.stopPropagation()}
                >
                  <FiMoreVertical />
                </IconButton>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content onClick={(e) => e.stopPropagation()}>
                    {/* Rotate is already the primary action for a token-less row (above) --
                        only surface it here too once a token is known, as the way to reissue it. */}
                    {token && (
                      <Menu.Item
                        value="rotate"
                        disabled={!origin || rotatingId === a.attendeeId}
                        onSelect={() => rotateLink(a.attendeeId)}
                      >
                        <HStack gap={2}>
                          {rotatingId === a.attendeeId ? <Spinner size="xs" /> : <FiRefreshCw />}
                          <Text>Rotate link</Text>
                        </HStack>
                      </Menu.Item>
                    )}
                    <Menu.Item
                      value="revoke"
                      color="red.600"
                      onSelect={() => openRevoke(a)}
                    >
                      <HStack gap={2}>
                        <FiSlash />
                        <Text>Revoke link</Text>
                      </HStack>
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </HStack>
        )
      },
    },
  ]

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (error) return <Text color="red.500">Failed to load attendees.</Text>

  return (
    <Box>
      {eventStatus === 'Draft' && (
        <Box bg="warning.subtle" color="warning.fg" px={3} py={2} borderRadius="md" mb={3} fontSize="sm">
          Attendee links are off — this event is a Draft. Publish it (top of the page) to turn them on.
        </Box>
      )}
      <HStack justify="space-between" mb={4}>
        <Text color="gray.600">
          {attendees?.length ?? 0} attendees
        </Text>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/dashboard/lobby-day/events/${eventId}/print-packets`)}
        >
          Print packets
        </Button>
      </HStack>

      <ResponsiveTable
        columns={columns}
        rows={attendees ?? []}
        keyFn={(a) => a.attendeeId}
        rowActions={[{
          label: 'Remove attendee',
          icon: FiTrash2,
          color: 'red.600',
          onClick: removeAttendee,
          isLoading: (a) => removingId === a.attendeeId,
        }]}
        emptyMessage="No attendees yet — add contacts to build the pool."
      />

      {openContactId && (
        <ContactDetailModal contactId={openContactId} isOpen onClose={() => setOpenContactId(null)} />
      )}

      <ResponsiveModal isOpen={!!revokeTarget} onClose={closeRevoke} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Revoke link for {revokeTarget?.fullName}?</Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} />
            </Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={3} align="stretch">
                {!!revokeError && <Text color="danger.fg" fontSize="sm">{revokeError}</Text>}
                <Text fontSize="sm" color="muted">
                  This revokes their schedule link immediately and signs out every device already on it.
                  They will need a fresh link to come back.
                </Text>
                <Field.Root>
                  <Field.Label>Reason (optional)</Field.Label>
                  <Textarea size="sm" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="outline" size="sm" minH="44px" onClick={closeRevoke}>Cancel</Button>
                <Button
                  variant="outline"
                  borderColor="danger.fg"
                  color="danger.fg"
                  size="sm"
                  minH="44px"
                  onClick={confirmRevoke}
                  loading={revokeMutation.isPending}
                >
                  Revoke link
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </Box>
  );
}
